import React from 'react';

import { Icon } from '@rbilabs/universal-components';
import { useRouter } from 'expo-router';
import { useFeatureNavigation } from 'features/content/hooks/use-feature-navigation';
import { ScrollView, StyleSheet, TouchableOpacity } from 'react-native';

import { Helmet } from 'components/helmet';
import LanguageSelector from 'components/language-selector';
import Link from 'components/link';
import { Box, Typography } from 'components/ui';
import { AccountLink } from 'pages/account/account-link';
import { AccountIcon } from 'pages/account/icons/account-icon';
import { CommunicationPreferencesIcon } from 'pages/account/icons/communication-preferences-icon';
import { PartnershipsIcon } from 'pages/account/icons/partnerships-icon';
import { PaymentMethodsIcon } from 'pages/account/icons/payment-methods-icon';
import { RecentOrdersIcon } from 'pages/account/icons/recent-orders-icon';
import { SubmitFeedbackIcon } from 'pages/account/icons/submit-feedback-icon';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useLoyaltyUserState } from 'state/loyalty/hooks/use-loyalty-user-state';
import { getDesignToken, primitive, spacing } from 'styles/constants';
import { regionEnum } from 'utils/environment';
import { Region } from 'utils/environment/types';
import { routes } from 'utils/routing';

import { AccountLoyaltyHeader } from './account-loyalty-header';
import Disclaimer from './disclaimer';
import ListOfLinks from './list-of-links';

const AccountAction = Link.withConfig({
  flexDirection: 'row',
  textDecoration: 'none',
  marginY: '2',
  alignItems: 'center',
});

export const AccountDrawerPage = ({ handleClose }: { handleClose: () => void }) => {
  useLoyaltyUserState({ refreshOnMountOrFocus: true });

  const enableFindRestaurantLink = useFlag(LaunchDarklyFlag.ENABLE_FIND_RESTAURANT_LINK);
  const enableWalmartLink = useFlag(LaunchDarklyFlag.ENABLE_WALMART_PLUS_INTEGRATION);
  const accountLinksWithNewBadge = useFlag(LaunchDarklyFlag.ENABLE_ACCOUNT_NEW_BADGE);
  const isLocalizationEnabled = regionEnum === Region.CA;
  const accountLinksWithNewBadgeEnabled = accountLinksWithNewBadge?.split(',') || [];
  const router = useRouter();

  const { isAuthenticated } = useAuthContext();

  const { aboutMenuEntries }: any = useFeatureNavigation();

  return (
    <>
      <Helmet title={t('myAccount')} />
      <TouchableOpacity
        onPress={handleClose}
        testID="modal-close-button"
        hitSlop={15}
        accessibilityLabel={t('close')}
        accessibilityRole="button"
        accessible
        focusable
      >
        <Icon variant="cancel" aria-hidden />
      </TouchableOpacity>
      <ScrollView showsVerticalScrollIndicator={false}>
        {isLocalizationEnabled && (
          <Box style={styles.accountLanguageSelectorContainer}>
            <Typography style={styles.languageSelectIntro}>{t('languageAndRegion')}</Typography>
            <LanguageSelector color={getDesignToken('text-default')} />
          </Box>
        )}
        <AccountLoyaltyHeader />
        {isAuthenticated && (
          <>
            <Box style={styles.accountLinksWrapper}>
              <AccountLink
                link={routes.accountInfo}
                label={t('accountInfo')}
                leftIcon={<AccountIcon />}
                testID="account-info"
                showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.accountInfo)}
              />
              <AccountLink
                link={routes.payment}
                label={t('paymentMethods')}
                leftIcon={<PaymentMethodsIcon />}
                testID="payment-methods"
                showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.payment)}
              />

              <AccountLink
                link={routes.orders}
                label={t('recentOrders')}
                leftIcon={<RecentOrdersIcon />}
                testID="recent-orders"
                showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.orders)}
              />

              <AccountLink
                link={routes.communication}
                label={t('communicationPreferences')}
                leftIcon={<CommunicationPreferencesIcon />}
                testID="communicationPreferences"
                showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.communication)}
              />
              {enableWalmartLink && (
                <AccountLink
                  link={routes.partnerships}
                  label={t('partnerships')}
                  leftIcon={<PartnershipsIcon />}
                  testID="partnerships"
                  showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.partnerships)}
                />
              )}
              <AccountLink
                link={routes.support}
                label={t('submitFeedback')}
                leftIcon={<SubmitFeedbackIcon />}
                linkProps={{ borderBottomWidth: 0 }}
                showNewBadge={accountLinksWithNewBadgeEnabled.includes(routes.support)}
              />
            </Box>
          </>
        )}

        <Box style={styles.actionsWrapper}>
          {enableFindRestaurantLink && (
            <AccountAction
              onPress={() =>
                router.replace({
                  pathname: routes.storeLocator,
                  params: { redirectUrl: routes.menu },
                })
              }
              justifyContent={'center'}
            >
              <Box>
                <Icon variant="location" color={primitive.$fieryRed} width="$6" aria-hidden />
              </Box>
              <Typography bold color={primitive.$fieryRed}>
                {t('findARestaurant')}
              </Typography>
            </AccountAction>
          )}
        </Box>

        {aboutMenuEntries && (
          <ListOfLinks
            testID="links-list"
            title={aboutMenuEntries.name?.locale || ''}
            entries={aboutMenuEntries.pages || []}
            borderColor="black"
            sectionHeadings
          />
        )}
        <Disclaimer />
        <Link to={routes.diagnostics}>
          <Typography>{t('diagnostics')}</Typography>
        </Link>
      </ScrollView>
    </>
  );
};

const styles = StyleSheet.create({
  accountLanguageSelectorContainer: {
    borderBottomColor: getDesignToken('border-color-default'),
    borderBottomWidth: 1,
    paddingBottom: spacing.$1,
  },
  accountLinksWrapper: {
    backgroundColor: primitive.$white,
    borderRadius: primitive.$borderRadius,
    marginBottom: spacing.$8,
    paddingHorizontal: spacing.$4,
    paddingVertical: spacing.$2,
  },
  actionsWrapper: {
    paddingBottom: spacing.$1,
  },
  languageSelectIntro: {
    marginBottom: spacing.$2,
    textTransform: 'none',
  },
});
